import { useState, useEffect } from 'react';
import { Button } from 'src/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'src/components/ui/card';
import { Label } from 'src/components/ui/label';
import { Switch } from 'src/components/ui/switch';
import { Separator } from 'src/components/ui/separator';
import { AlertContainer, AlertDescription } from 'src/components/ui/alert';
import { Badge } from 'src/components/ui/badge';
import {
  CircleNotchIcon,
  CheckCircleIcon,
  WarningCircleIcon,
  ArrowsClockwiseIcon,
} from '@phosphor-icons/react';
import {
  useGetIntegration,
  useUpdateIntegration,
} from 'src/api/rpc/hooks/integrations/useIntegrations';
import {
  IntegrationAvailability,
  IntegrationKey,
  FamousIntegrationSettings as FamousSettings,
} from '@erp/rpc';
import { useToast } from 'src/context/ToastContext';
import { useTriggerRefetchAllData } from 'src/api/core/useTriggerRefetchAllData';
import { getIntegrationConfig } from '@/config/integrations';
import { GlJournalTestCard } from './components/GlJournalTestCard';
import { T, useGT } from 'gt-react';

export function FamousIntegrationSettings({
  availability: _availability,
}: {
  availability?: IntegrationAvailability;
}) {
  const gt = useGT();
  const { showToast } = useToast();
  const {
    data: integration,
    isLoading,
    error,
  } = useGetIntegration(IntegrationKey.Famous);
  const updateIntegration = useUpdateIntegration();
  const triggerRefetch = useTriggerRefetchAllData();

  // Local state for form
  const [formData, setFormData] = useState<FamousSettings>(
    getIntegrationConfig(IntegrationKey.Famous).defaultSettings,
  );
  const [isSyncing, setIsSyncing] = useState(false);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  // Initialize form data when integration data loads
  useEffect(() => {
    if (integration?.settings) {
      setFormData(integration.settings as FamousSettings);
    }
  }, [integration]);

  const handleSettingChange = async <T extends keyof FamousSettings>(
    section: T,
    key: keyof FamousSettings[T],
    value: boolean,
  ) => {
    const newFormData = {
      ...formData,
      [section]: {
        ...formData[section],
        [key]: value,
      },
    };

    setFormData(newFormData);
    setIsSavingSettings(true);

    // Auto-save the settings immediately
    try {
      await updateIntegration.mutateAsync({
        key: IntegrationKey.Famous,
        settings: newFormData,
      });

      showToast({
        title: gt('Setting Updated', { context: 'Famous integration toast' }),
        message: gt('Your setting has been saved automatically.', {
          context: 'Famous integration toast',
        }),
        color: 'success',
        icon: 'check',
      });
    } catch (error) {
      // Revert the change if save failed
      setFormData(formData);
      showToast({
        title: gt('Save Failed', { context: 'Famous integration toast' }),
        message: gt('Failed to save setting. Please try again.', {
          context: 'Famous integration toast',
        }),
        color: 'error',
        icon: 'error',
      });
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      await triggerRefetch.mutateAsync();
      showToast({
        title: gt('Data Pull Started', { context: 'Famous integration toast' }),
        message: gt('Data pull has been triggered successfully.', {
          context: 'Famous integration toast',
        }),
        color: 'success',
        icon: 'check',
      });
    } catch (error) {
      showToast({
        title: gt('Data Pull Failed', { context: 'Famous integration toast' }),
        message: gt('Failed to trigger data pull. Please try again.', {
          context: 'Famous integration toast',
        }),
        color: 'error',
        icon: 'error',
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const handleTestConnection = async () => {
    setIsTestingConnection(true);
    try {
      // For now, we'll use a simple timeout to simulate a connection test
      // In a real implementation, you would make an API call to test the connection
      await new Promise((resolve) => setTimeout(resolve, 2000));

      showToast({
        title: gt('Connection Test Successful', {
          context: 'Famous integration toast',
        }),
        message: gt('FAMOUS™ integration is connected and working properly.', {
          context: 'Famous integration toast',
        }),
        color: 'success',
        icon: 'check',
      });
    } catch (error) {
      showToast({
        title: gt('Connection Test Failed', {
          context: 'Famous integration toast',
        }),
        message: gt(
          'Unable to connect to FAMOUS™. Please check your configuration.',
          { context: 'Famous integration toast' },
        ),
        color: 'error',
        icon: 'error',
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <CircleNotchIcon className="h-6 w-6 animate-spin mr-2" />
        <span>
          <T context="Famous integration loading">
            Loading integration settings...
          </T>
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <AlertContainer variant="destructive">
        <WarningCircleIcon className="h-4 w-4" />
        <AlertDescription>
          <T context="Famous integration load error">
            Failed to load integration settings. Please try refreshing the page.
          </T>
        </AlertDescription>
      </AlertContainer>
    );
  }

  return (
    <div className="space-y-6">
      {/* Integration Status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <CheckCircleIcon className="h-5 w-5 text-green-600" />
            <span className="font-medium">
              <T context="Famous integration status">Integration Active</T>
            </span>
          </div>
          <Badge
            variant="outline"
            className="bg-green-100 text-green-700 border-green-300"
          >
            <T context="integration connected badge">Connected</T>
          </Badge>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleTestConnection}
          disabled={isTestingConnection}
        >
          {isTestingConnection ? (
            <>
              <CircleNotchIcon className="h-4 w-4 mr-2 animate-spin" />
              <T context="testing Famous connection">Testing...</T>
            </>
          ) : (
            <>
              <ArrowsClockwiseIcon className="h-4 w-4 mr-2" />
              <T context="test Famous integration connection button">
                Test Connection
              </T>
            </>
          )}
        </Button>
      </div>

      <Separator />

      {/* Product Mapping Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            <T context="Famous integration card">Product Mapping</T>
          </CardTitle>
          <CardDescription>
            <T context="Famous integration product mapping description">
              Configure how products are synchronized between FAMOUS™ and your
              system
            </T>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label
                htmlFor="use-customer-products"
                className="text-base font-medium"
              >
                <T context="Famous product mapping toggle">
                  Use Customer Products
                </T>
                {isSavingSettings && (
                  <CircleNotchIcon className="h-4 w-4 ml-2 inline animate-spin" />
                )}
              </Label>
              <p className="text-sm text-gray-600">
                <T context="Famous product mapping help">
                  When enabled, customer-specific product mappings will be used
                  instead of global product mappings
                </T>
              </p>
            </div>
            <Switch
              id="use-customer-products"
              checked={formData.productMapping.useCustomerProducts}
              disabled={isSavingSettings}
              onCheckedChange={(checked) =>
                handleSettingChange(
                  'productMapping',
                  'useCustomerProducts',
                  checked,
                )
              }
            />
          </div>

          {formData.productMapping.useCustomerProducts && (
            <AlertContainer>
              <AlertDescription>
                <T context="Famous customer product mapping info">
                  Customer product mapping is enabled. Product synchronization
                  will use customer-specific mappings when available.
                </T>
              </AlertDescription>
            </AlertContainer>
          )}
        </CardContent>
      </Card>

      {/* Sales Orders Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            <T context="Famous integration card">Sales Orders</T>
          </CardTitle>
          <CardDescription>
            <T context="Famous sales orders description">
              Configure how sales orders are processed and synchronized
            </T>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label
                htmlFor="remove-zero-quantity-items"
                className="text-base font-medium"
              >
                <T context="Famous sales order toggle">
                  Remove Zero Quantity Line Items on Approval
                </T>
                {isSavingSettings && (
                  <CircleNotchIcon className="h-4 w-4 ml-2 inline animate-spin" />
                )}
              </Label>
              <p className="text-sm text-gray-600">
                <T context="Famous sales order toggle help">
                  When enabled, line items with zero quantity will be
                  automatically removed when approving sales order drafts
                </T>
              </p>
            </div>
            <Switch
              id="remove-zero-quantity-items"
              checked={
                formData.salesOrders.removeZeroQuantityLineItemsOnApproval
              }
              disabled={isSavingSettings}
              onCheckedChange={(checked) =>
                handleSettingChange(
                  'salesOrders',
                  'removeZeroQuantityLineItemsOnApproval',
                  checked,
                )
              }
            />
          </div>

          {formData.salesOrders.removeZeroQuantityLineItemsOnApproval && (
            <AlertContainer>
              <AlertDescription>
                <T context="Famous zero quantity line items info">
                  Zero quantity line items will be automatically filtered out
                  during sales order approval. This helps maintain clean sales
                  orders by removing invalid or cancelled line items.
                </T>
              </AlertDescription>
            </AlertContainer>
          )}
        </CardContent>
      </Card>

      {/* Sync Operations */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            <T context="Famous integration card">Data Synchronization</T>
          </CardTitle>
          <CardDescription>
            <T context="Famous data sync description">
              Manage data synchronization operations with FAMOUS™
            </T>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex justify-center">
            <Button
              variant="outline"
              className="h-auto p-6 min-w-[200px]"
              onClick={handleSync}
              disabled={isSyncing}
            >
              <div className="text-center">
                {isSyncing ? (
                  <CircleNotchIcon className="h-6 w-6 mx-auto mb-2 animate-spin" />
                ) : (
                  <ArrowsClockwiseIcon className="h-6 w-6 mx-auto mb-2" />
                )}
                <div className="font-medium">
                  {isSyncing ? (
                    <T context="Famous data pull in progress">Triggering...</T>
                  ) : (
                    <T context="Famous trigger data pull button">
                      Trigger Data Pull
                    </T>
                  )}
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  <T context="Famous data pull helper">
                    Pull all data from integrated systems
                  </T>
                </div>
              </div>
            </Button>
          </div>

          <AlertContainer>
            <WarningCircleIcon className="h-4 w-4" />
            <AlertDescription>
              <T context="Famous data pull rate limit warning">
                Data pull can only be triggered once every 2 hours and may take
                a long time to complete.
              </T>
            </AlertDescription>
          </AlertContainer>
        </CardContent>
      </Card>

      {/* GL Journal Test */}
      <GlJournalTestCard />

      {/* Note: Settings are auto-saved when changed */}
    </div>
  );
}
